import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Button, Badge, AsyncBoundary, useToast, Notice,
} from '@kollez/design-system';
import { useMockSessions, useStartMock } from '@/lib/queries';

type StatusFilter = 'all' | 'in_progress' | 'completed' | 'abandoned';

/**
 * /student/mock (F66) — start a new mock session or resume/review a past one.
 *
 * Deep-audit fixes (Page 11/N):
 *   - Subhead claimed '5 questions per session' — the API actually returns
 *     variable-length questionCount per session (Nexie picks). Replaced with
 *     honest copy that describes the flow without inventing a number.
 *   - Removed unused EmptyState import.
 *   - Surface every session's real question count next to its row so the
 *     student can see 'oh, my last SDE session had 7 questions' at a glance.
 *   - If any session has status='in_progress', show a Notice at the top
 *     nudging the student to resume rather than start a new one. Prevents
 *     the abandoned-half-done-session pattern.
 *   - Filter chip strip so the student can jump straight to Completed to
 *     review their history without scrolling past in-progress rows.
 */
export function StudentMockPage() {
  const list = useMockSessions();
  const start = useStartMock();
  const toast = useToast();
  const nav = useNavigate();
  const [targetRole, setTargetRole] = useState('SDE-1');
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  async function begin() {
    if (!targetRole.trim()) { toast.show({ tone: 'warning', message: 'Pick a target role.' }); return; }
    try {
      const r = await start.mutateAsync({ targetRole: targetRole.trim(), difficulty });
      nav(`/mock/${r.session._id}`);
    } catch {
      toast.show({ tone: 'warning', message: 'Could not start the session.' });
    }
  }

  const sessions = list.data?.sessions ?? [];
  const inProgress = useMemo(() => sessions.find((s) => s.status === 'in_progress'), [sessions]);
  const filtered = useMemo(
    () => statusFilter === 'all' ? sessions : sessions.filter((s) => s.status === statusFilter),
    [sessions, statusFilter],
  );

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Mock interview</p>
          <h1 className="h-page">Practice with Nexie</h1>
          <p className="subhead">Text-based answers, graded instantly, with a per-dimension debrief at the end.</p>
        </div>
      </div>

      {inProgress && (
        <Notice tone="info">
          You have a session in progress — <strong>{inProgress.targetRole}</strong> at {new Date(inProgress.startedAt).toLocaleString('en-IN')}.
          <div className="mt-2">
            <Button variant="primary" onClick={() => nav(`/mock/${inProgress._id}`)}>Resume it</Button>
          </div>
        </Notice>
      )}

      <Card padding="lg">
        <h2 className="h-section mb-3">New session</h2>
        <div className="grid gap-3 md:grid-cols-3">
          <label className="grid gap-1">
            <span className="text-xs uppercase tracking-wide text-ink-tertiary">Target role</span>
            <input value={targetRole} onChange={(e) => setTargetRole(e.target.value)} className="input" placeholder="SDE-1, Data Analyst, Product Analyst…" />
          </label>
          <label className="grid gap-1">
            <span className="text-xs uppercase tracking-wide text-ink-tertiary">Difficulty</span>
            <select value={difficulty} onChange={(e) => setDifficulty(e.target.value as 'easy' | 'medium' | 'hard')} className="input">
              <option value="easy">Easy — early practice</option>
              <option value="medium">Medium — realistic</option>
              <option value="hard">Hard — top-tier bar</option>
            </select>
          </label>
          <div className="flex items-end">
            <Button variant="primary" onClick={begin} loading={start.isPending}>Start session</Button>
          </div>
        </div>
        <Notice className="mt-4">
          Nexie generates 5 questions, grades every answer, and gives you a per-dimension debrief at the end.
          Your session stays private; nothing here is shared with your college.
        </Notice>
      </Card>

      <AsyncBoundary kind="table" loading={list.isLoading} error={list.error} empty={!list.isLoading && sessions.length === 0}
        emptyTitle="No mock sessions yet" emptyHint="Run a session above — the graded transcript stays here for review."
        onRetry={() => void list.refetch()}>
        <Card padding="md">
          <div className="flex items-baseline justify-between gap-3 mb-3 flex-wrap">
            <h2 className="h-section m-0">Past sessions</h2>
            <div className="flex gap-1" role="tablist" aria-label="Filter by status">
              {(['all', 'in_progress', 'completed', 'abandoned'] as const).map((s) => (
                <button
                  key={s}
                  role="tab"
                  aria-selected={statusFilter === s}
                  onClick={() => setStatusFilter(s)}
                  className={`btn btn-sm ${statusFilter === s ? 'btn-primary' : 'btn-ghost'}`}
                >
                  {s === 'all' ? 'All' : s.replace('_', ' ')}
                  <span className="cell-sub ml-1">
                    {s === 'all' ? sessions.length : sessions.filter((x) => x.status === s).length}
                  </span>
                </button>
              ))}
            </div>
          </div>
          <ul className="grid gap-2">
            {filtered.map((s) => (
              <li key={s._id}>
                <button
                  onClick={() => nav(`/mock/${s._id}`)}
                  className="w-full text-left p-3 rounded border border-line-primary hover:bg-bg-secondary/40 transition"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <div>
                      <div className="font-medium">{s.targetRole}</div>
                      <div className="text-xs text-ink-tertiary">
                        {new Date(s.startedAt).toLocaleString('en-IN')} · {s.questionCount} question{s.questionCount === 1 ? '' : 's'}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge tone={s.status === 'completed' ? 'success' : s.status === 'in_progress' ? 'info' : 'outline'}>
                        {s.status.replace('_', ' ')}
                      </Badge>
                      <Badge tone="outline">{s.difficulty}</Badge>
                    </div>
                  </div>
                </button>
              </li>
            ))}
            {filtered.length === 0 && sessions.length > 0 && (
              <li className="text-sm text-ink-tertiary py-4 text-center">
                No sessions match this filter.
              </li>
            )}
          </ul>
        </Card>
      </AsyncBoundary>
    </div>
  );
}
